/** A code repository on GitHub or GitLab, and how one is written down. */

export type PlatformProvider = 'github' | 'gitlab'

export interface RepositoryRef {
  provider: PlatformProvider
  owner: string
  name: string
  repository: string
  host: string
  ref?: string
}

/** Accepts "owner/name", a repository address (GitHub / GitLab), or an address with extra path. */
export function parseRepositoryRef(input: string): RepositoryRef | undefined {
  const trimmed = input
    .trim()
    .replace(/\.git$/, '')
    .replace(/\/+$/, '')
  if (!trimmed) return undefined

  const [, user, repo] = /^([\w.-]+)\/([\w.-]+)$/.exec(trimmed) ?? []
  if (user && repo) {
    return {
      provider: 'github',
      owner: user,
      name: repo,
      repository: `${user}/${repo}`,
      host: 'github.com',
      ref: 'HEAD',
    }
  }

  try {
    const url = new URL(trimmed.includes('://') ? trimmed : `https://${trimmed}`)
    const host = url.hostname.toLowerCase()
    const isGitLab = host.includes('gitlab')
    const isGitHub = host.includes('github') || (!isGitLab && !host.includes('.'))

    const parts = url.pathname.split('/').filter(Boolean)
    const [owner, name] = parts
    if (!owner || !name) return undefined

    if (isGitLab) {
      const dashIndex = parts.indexOf('-')
      const projectParts = dashIndex >= 0 ? parts.slice(0, dashIndex) : parts
      if (projectParts.length < 2) return undefined

      let branch: string | undefined
      if (dashIndex >= 0 && parts[dashIndex + 1] === 'tree' && parts[dashIndex + 2]) {
        branch = parts[dashIndex + 2]
      }

      return {
        provider: 'gitlab',
        owner: projectParts.slice(0, -1).join('/'),
        name: projectParts[projectParts.length - 1]!,
        repository: projectParts.join('/'),
        host,
        ref: branch ?? 'main',
      }
    }

    if (isGitHub) {
      let branch: string | undefined
      if (parts[2] === 'tree' && parts[3]) {
        branch = parts[3]
      }
      return {
        provider: 'github',
        owner,
        name,
        repository: `${owner}/${name}`,
        host: 'github.com',
        ref: branch ?? 'HEAD',
      }
    }

    return {
      provider: 'github',
      owner,
      name,
      repository: `${owner}/${name}`,
      host,
      ref: 'HEAD',
    }
  } catch {
    // Not an address.
  }
  return undefined
}
